/**
 * Le calendrier : des jours relatifs aux dates réelles.
 *
 * *PRD §4.5 : « dès que le chantier porte une date de démarrage — ferme ou
 * hypothèse —, le Gantt s'affiche calé sur le calendrier réel ; les jours relatifs
 * ne subsistent que pour les chantiers sans date. »*
 * *PRD §4.4 : « samedis et dimanches non travaillés, jours fériés gérés (calendrier
 * français, paramétrable). »*
 *
 * Le planning se saisit en **jours relatifs** — J1 est le premier jour du chantier —
 * et c'est ce que la base conserve (`phase.debut_relatif`, `livraison.jour_relatif`).
 * Ce fichier ne fait qu'une chose : dire quelle date porte J-n. La saisie ne change
 * pas de nature parce qu'une date apparaît ; c'est un affichage, et il se recalcule
 * en entier quand la date de démarrage bouge — d'où le « recalage global » du PRD,
 * qui n'est ici même pas un recalcul, juste une autre lecture.
 *
 * ## Pourquoi aucun objet de date n'apparaît ici
 *
 * Ce n'est pas seulement le contrat de pureté du moteur (architecture §3, décision
 * T7) qui l'interdit : c'est une leçon payée. Sur l'autre application du groupe, un
 * calcul de jours ouvrés mélangeait dates locales et conversion en temps universel ;
 * en UTC+2, le 1er mai, le 14 juillet, le 11 novembre et Noël étaient comptés comme
 * ouvrés — 254 jours ouvrés au lieu de 252 sur l'année. Les fériés *mobiles*
 * passaient, leur double conversion se compensant : d'où la discrétion du défaut.
 *
 * Une date civile n'a pas d'heure, donc pas de fuseau. Ici elle est ce qu'elle est :
 * une chaîne `AAAA-MM-JJ`, celle que PostgreSQL rend pour une colonne `date` et
 * celle qu'un `<input type="date">` produit. Toute l'arithmétique passe par un
 * **numéro de jour** entier, et rien d'autre. Aucun décalage horaire ne peut s'y
 * glisser, à aucune heure, dans aucun pays.
 *
 * ## Le calendrier est paramétrable, par EXCEPTIONS
 *
 * *Demande de Chris, 15/08/2026 : « l'admin pourra paramétrer des jours non
 * travaillés en plus des jours fériés. »*
 *
 * Les onze fériés restent **calculés** — Pâques comprise, donc justes pour toutes les
 * années à venir sans qu'on ressaisisse rien. Ce que l'administration pose, ce sont
 * les écarts, et **dans les deux sens** : un jour chômé en plus (pont, fermeture
 * d'août), un jour travaillé exceptionnellement. Ce second sens est le « samedi
 * exceptionnel » du PRD §4.4, qui n'avait jusqu'ici aucun point d'entrée.
 *
 * *La première version acceptait un jeu de jours non ouvrés qui **remplaçait** le
 * calendrier français. Passer trois jours de fermeture aurait donc fait travailler le
 * 14 juillet : l'exact inverse de « en plus des jours fériés », et un défaut
 * silencieux — un planning décalé d'un jour ne ressemble pas à une erreur. Aucun
 * appelant ne s'en servait, le remplacement a donc cédé la place à l'ajout.*
 */
const FORME = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * La date est-elle une date, et non seulement une chaîne bien formée ?
 *
 * `2026-02-30` a la bonne forme et n'existe pas. Le contrôle se fait par
 * aller-retour : on convertit en numéro de jour puis on reconvertit, et une date
 * inexistante ne redonne pas la chaîne de départ — février 30 revient en mars 2.
 * C'est plus court qu'une table des longueurs de mois et ça traite les années
 * bissextiles sans les nommer.
 */
export function estDateCivile(valeur) {
    const parties = relire(valeur);
    if (parties === null)
        return false;
    return depuisNumeroDeJour(numeroDeJour(parties)) === valeur;
}
function relire(valeur) {
    const trouve = FORME.exec(valeur);
    if (trouve === null)
        return null;
    const annee = Number(trouve[1]);
    const mois = Number(trouve[2]);
    const jour = Number(trouve[3]);
    if (mois < 1 || mois > 12 || jour < 1 || jour > 31)
        return null;
    return { annee, mois, jour };
}
/** Relit une date civile, ou refuse. Le message nomme la valeur reçue. */
export function partiesDeDate(date) {
    const parties = relire(date);
    if (parties === null || depuisNumeroDeJour(numeroDeJour(parties)) !== date) {
        throw new Error(`« ${date} » n'est pas une date au format AAAA-MM-JJ.`);
    }
    return parties;
}
/* ---------------------------------------------------------------------------
 * L'arithmétique : un jour est un entier
 * ------------------------------------------------------------------------- */
/**
 * Le numéro d'un jour, compté depuis le 1er janvier 1970.
 *
 * Algorithme de Howard Hinnant (`days_from_civil`), qui déplace l'origine de l'année
 * au 1er mars : février, et donc le jour bissextile, tombe alors en fin de cycle et
 * cesse d'être un cas particulier. Le calendrier grégorien étant périodique sur 400
 * ans (146 097 jours), le reste est une division.
 *
 * Le résultat est négatif avant 1970. Ça n'a aucun usage dans le produit, mais les
 * fonctions ci-dessous n'ont alors pas à s'en préoccuper.
 */
export function numeroDeJour(parties) {
    const { mois, jour } = parties;
    const annee = parties.annee - (mois <= 2 ? 1 : 0);
    const ere = Math.floor(annee / 400);
    const anneeDansLEre = annee - ere * 400;
    const jourDansLAnnee = Math.floor((153 * (mois + (mois > 2 ? -3 : 9)) + 2) / 5) + jour - 1;
    const jourDansLEre = anneeDansLEre * 365 +
        Math.floor(anneeDansLEre / 4) -
        Math.floor(anneeDansLEre / 100) +
        jourDansLAnnee;
    return ere * 146097 + jourDansLEre - 719468;
}
/** L'inverse exact de `numeroDeJour` (`civil_from_days`). */
export function depuisNumeroDeJour(numero) {
    const decale = numero + 719468;
    const ere = Math.floor(decale / 146097);
    const jourDansLEre = decale - ere * 146097;
    const anneeDansLEre = Math.floor((jourDansLEre -
        Math.floor(jourDansLEre / 1460) +
        Math.floor(jourDansLEre / 36524) -
        Math.floor(jourDansLEre / 146096)) /
        365);
    const annee = anneeDansLEre + ere * 400;
    const jourDansLAnnee = jourDansLEre -
        (365 * anneeDansLEre +
            Math.floor(anneeDansLEre / 4) -
            Math.floor(anneeDansLEre / 100));
    const moisDecale = Math.floor((5 * jourDansLAnnee + 2) / 153);
    const jour = jourDansLAnnee - Math.floor((153 * moisDecale + 2) / 5) + 1;
    const mois = moisDecale + (moisDecale < 10 ? 3 : -9);
    return ecrire(annee + (mois <= 2 ? 1 : 0), mois, jour);
}
function ecrire(annee, mois, jour) {
    return `${String(annee).padStart(4, "0")}-${String(mois).padStart(2, "0")}-${String(jour).padStart(2, "0")}`;
}
/** Ajoute des jours de calendrier — fériés et week-ends compris. */
export function ajouterJours(date, nombre) {
    return depuisNumeroDeJour(numeroDeJour(partiesDeDate(date)) + nombre);
}
/**
 * Le jour de la semaine, 1 pour lundi jusqu'à 7 pour dimanche (norme ISO 8601).
 *
 * Le 1er janvier 1970 était un jeudi, d'où le décalage de 3. Le modulo est ramené
 * dans le positif : en JavaScript, `-1 % 7` vaut `-1` et non `6`, ce qui ferait
 * répondre « jour 0 » sur une date d'avant 1970.
 */
export function jourDeLaSemaine(date) {
    const numero = numeroDeJour(partiesDeDate(date));
    return (((numero + 3) % 7) + 7) % 7 + 1;
}
export const SAMEDI = 6;
export const DIMANCHE = 7;
/* ---------------------------------------------------------------------------
 * Les jours fériés
 * ------------------------------------------------------------------------- */
/**
 * Le dimanche de Pâques d'une année, dans le calendrier grégorien.
 *
 * Algorithme dit « grégorien anonyme » (Meeus / Jones / Butcher) : de l'arithmétique
 * entière et rien d'autre, valable de 1583 à 4099. Il est ici parce que **quatre des
 * onze fériés français en dépendent** — lundi de Pâques, Ascension, lundi de
 * Pentecôte, et l'écart entre eux est fixe. Les tabuler année par année serait une
 * table à maintenir, et une année manquante rendrait quatre jours ouvrés d'un coup.
 */
export function paques(annee) {
    const a = annee % 19;
    const b = Math.floor(annee / 100);
    const c = annee % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const total = h + l - 7 * m + 114;
    return ecrire(annee, Math.floor(total / 31), (total % 31) + 1);
}
/**
 * Les onze jours fériés français d'une année, avec leur nom.
 *
 * Le nom voyage avec la date parce que l'écran en a besoin : « 14 juillet » sur
 * l'infobulle d'une colonne grisée se comprend d'un coup d'œil, là où une case grise
 * sans explication ressemble à un défaut d'affichage.
 *
 * L'Alsace-Moselle en compte deux de plus (Vendredi saint, 26 décembre). Ils ne sont
 * pas ici : ils entrent par les **exceptions** que les fonctions ci-dessous
 * acceptent — un jour chômé de plus —, jamais par un cas particulier codé en dur
 * dans cette liste.
 */
export function feriesFrancais(annee) {
    const dimancheDePaques = paques(annee);
    const depuisPaques = (jours) => ajouterJours(dimancheDePaques, jours);
    return new Map([
        [ecrire(annee, 1, 1), "Jour de l'an"],
        [depuisPaques(1), "Lundi de Pâques"],
        [ecrire(annee, 5, 1), "Fête du travail"],
        [ecrire(annee, 5, 8), "Victoire 1945"],
        [depuisPaques(39), "Ascension"],
        [depuisPaques(50), "Lundi de Pentecôte"],
        [ecrire(annee, 7, 14), "Fête nationale"],
        [ecrire(annee, 8, 15), "Assomption"],
        [ecrire(annee, 11, 1), "Toussaint"],
        [ecrire(annee, 11, 11), "Armistice 1918"],
        [ecrire(annee, 12, 25), "Noël"],
    ]);
}
/**
 * Le cache des fériés par année.
 *
 * Une frise de cent jours interroge le calendrier des centaines de fois par rendu,
 * sur deux ou trois années au plus. Recalculer Pâques à chaque appel est sans danger
 * — la fonction est pure — mais inutilement répété. Le cache ne change aucun
 * résultat : la même année rend le même jeu, toujours.
 */
const parAnnee = new Map();
function feriesDeLAnnee(annee) {
    const connus = parAnnee.get(annee);
    if (connus !== undefined)
        return connus;
    const calcules = feriesFrancais(annee);
    parAnnee.set(annee, calcules);
    return calcules;
}
/** Le nom du férié qui tombe ce jour-là, ou `null`. */
export function ferie(date) {
    return feriesDeLAnnee(partiesDeDate(date).annee).get(date) ?? null;
}
/** Le calendrier français nu : week-ends et onze fériés, aucun écart. */
export const SANS_EXCEPTION = {
    chomes: new Set(),
    travailles: new Set(),
};
/**
 * Le calendrier que forment des exceptions telles que la base les porte.
 *
 * Une seule traversée pour les deux ensembles, et surtout **un seul endroit** où la
 * convention « `chome` vrai = chômé » se lit. Répartie chez chaque appelant, elle
 * finirait par s'inverser quelque part, et un pont deviendrait un jour travaillé.
 */
export function calendrierDesExceptions(exceptions) {
    const chomes = new Set();
    const travailles = new Set();
    for (const exception of exceptions) {
        (exception.chome ? chomes : travailles).add(exception.jour);
    }
    return { chomes, travailles };
}
/**
 * Ce qui empêche ce jour d'être travaillé, ou `null`.
 *
 * **L'ordre des tests est la règle métier**, et il se lit ici : un jour déclaré
 * travaillé l'est, quoi qu'en dise le calendrier — c'est la définition même d'une
 * exception, et la placer après le week-end la rendrait inopérante précisément sur
 * les jours pour lesquels elle a été demandée.
 */
export function empechement(date, calendrier) {
    if (calendrier?.travailles.has(date) === true)
        return null;
    const semaine = jourDeLaSemaine(date);
    if (semaine === SAMEDI || semaine === DIMANCHE)
        return "week-end";
    const nom = ferie(date);
    if (nom !== null)
        return { ferie: nom };
    return calendrier?.chomes.has(date) === true ? { ferie: "Jour chômé" } : null;
}
/**
 * Le jour est-il travaillé ?
 *
 * Sans calendrier, les week-ends et les onze fériés français — ce que fait tout le
 * produit tant qu'aucune exception n'est posée.
 */
export function estOuvre(date, calendrier) {
    return empechement(date, calendrier) === null;
}
/** Le premier jour ouvré à partir de cette date — elle-même si elle l'est. */
export function prochainJourOuvre(date, calendrier) {
    let courant = date;
    // Sept essais suffisent en semaine ; la borne est large pour survivre à un jeu de
    // jours non ouvrés absurde (« tout le mois est fermé ») sans boucler sans fin.
    for (let essais = 0; essais < 400; essais += 1) {
        if (estOuvre(courant, calendrier))
            return courant;
        courant = ajouterJours(courant, 1);
    }
    throw new Error(`Aucun jour ouvré trouvé dans les 400 jours suivant le ${date} : le calendrier fourni les exclut tous.`);
}
/* ---------------------------------------------------------------------------
 * Des jours relatifs aux dates
 * ------------------------------------------------------------------------- */
/**
 * Les dates des `nombre` premiers jours ouvrés du chantier, J1 en tête.
 *
 * **J1 est la date de démarrage, ou le premier jour ouvré qui la suit.** Une date de
 * démarrage tombant un dimanche est une saisie qui arrive — on choisit le lundi
 * plutôt que de refuser la saisie ou de perdre un jour de planning. Le décalage est
 * visible à l'écran, puisque la frise affiche la date.
 *
 * Rendu en une passe pour toute la frise plutôt qu'une date à la fois : la frise
 * demande ses cent colonnes d'affilée, et appeler cent fois une fonction qui
 * recompte depuis J1 ferait dix mille tours de boucle pour cent dates.
 */
export function datesDesJoursOuvres(dateDebut, nombre, calendrier) {
    if (!Number.isInteger(nombre) || nombre < 0) {
        throw new Error(`Un nombre de jours se compte en entiers positifs, reçu ${nombre}.`);
    }
    const dates = [];
    let courant = prochainJourOuvre(dateDebut, calendrier);
    for (let rang = 0; rang < nombre; rang += 1) {
        dates.push(courant);
        courant = prochainJourOuvre(ajouterJours(courant, 1), calendrier);
    }
    return dates;
}
/**
 * La date du jour relatif J-n, `jourRelatif` commençant à 1.
 *
 * À n'employer que pour une date isolée — l'infobulle d'une barre, l'échéance d'une
 * livraison. Pour une frise entière, `datesDesJoursOuvres` fait le même travail en
 * une passe.
 */
export function dateDuJourRelatif(dateDebut, jourRelatif, calendrier) {
    if (!Number.isInteger(jourRelatif) || jourRelatif < 1) {
        throw new Error(`Un jour relatif commence à 1, reçu ${jourRelatif}.`);
    }
    const dates = datesDesJoursOuvres(dateDebut, jourRelatif, calendrier);
    const date = dates[jourRelatif - 1];
    if (date === undefined)
        throw new Error("Jour relatif hors de la frise calculée.");
    return date;
}
/**
 * Le jour relatif d'une date, ou `null` si elle n'est pas ouvrée.
 *
 * L'inverse de `dateDuJourRelatif`, pour le geste qui viendra : cliquer un jour dans
 * un calendrier pour y poser une livraison. Rend `null` plutôt que le jour ouvré
 * voisin — déplacer silencieusement une livraison d'un jour parce que l'utilisateur
 * a cliqué un samedi serait un mensonge d'un jour sur une échéance.
 */
export function jourRelatifDeLaDate(dateDebut, date, calendrier) {
    if (!estOuvre(date, calendrier))
        return null;
    const premier = prochainJourOuvre(dateDebut, calendrier);
    const cible = numeroDeJour(partiesDeDate(date));
    if (cible < numeroDeJour(partiesDeDate(premier)))
        return null;
    let courant = premier;
    let rang = 1;
    while (numeroDeJour(partiesDeDate(courant)) < cible) {
        courant = prochainJourOuvre(ajouterJours(courant, 1), calendrier);
        rang += 1;
    }
    return courant === date ? rang : null;
}
/* ---------------------------------------------------------------------------
 * L'écriture
 * ------------------------------------------------------------------------- */
const JOURS_ABREGES = ["lun.", "mar.", "mer.", "jeu.", "ven.", "sam.", "dim."];
const JOURS = [
    "lundi",
    "mardi",
    "mercredi",
    "jeudi",
    "vendredi",
    "samedi",
    "dimanche",
];
const MOIS = [
    "janvier",
    "février",
    "mars",
    "avril",
    "mai",
    "juin",
    "juillet",
    "août",
    "septembre",
    "octobre",
    "novembre",
    "décembre",
];
/**
 * Formaté à la main, sans passer par le formateur du navigateur.
 *
 * `Intl` demanderait un objet de date — donc une heure, donc un fuseau, donc le
 * défaut que ce fichier existe pour éviter. Et le serveur et le navigateur
 * rendraient deux libellés différents selon la langue du système.
 */
/** `13/08/2026` — la forme courte française. */
export function formaterDate(date) {
    const { annee, mois, jour } = partiesDeDate(date);
    return `${String(jour).padStart(2, "0")}/${String(mois).padStart(2, "0")}/${annee}`;
}
/** `jeu. 13/08` — pour une colonne de frise, où l'année est déjà dite ailleurs. */
export function formaterDateCourte(date) {
    const { mois, jour } = partiesDeDate(date);
    const abrege = JOURS_ABREGES[jourDeLaSemaine(date) - 1] ?? "";
    return `${abrege} ${String(jour).padStart(2, "0")}/${String(mois).padStart(2, "0")}`;
}
/** `jeudi 13 août 2026` — pour une infobulle, où la place ne manque pas. */
export function formaterDateLongue(date) {
    const { annee, mois, jour } = partiesDeDate(date);
    const nomDuJour = JOURS[jourDeLaSemaine(date) - 1] ?? "";
    const nomDuMois = MOIS[mois - 1] ?? "";
    return `${nomDuJour} ${jour} ${nomDuMois} ${annee}`;
}
/** Le mois d'une date. `2026-09-14` → `2026-09`. */
export function moisDeLaDate(date) {
    const { annee, mois } = partiesDeDate(date);
    return `${String(annee).padStart(4, "0")}-${String(mois).padStart(2, "0")}`;
}
/** Les deux nombres d'un mois, ou une erreur : un mois mal formé ne se devine pas. */
function partiesDuMois(mois) {
    const trouve = /^(\d{4})-(\d{2})$/u.exec(mois);
    if (trouve === null)
        throw new Error(`Un mois s'écrit « AAAA-MM », reçu « ${mois} ».`);
    const annee = Number(trouve[1]);
    const numero = Number(trouve[2]);
    if (numero < 1 || numero > 12)
        throw new Error(`Mois hors bornes : « ${mois} ».`);
    return { annee, numero };
}
/**
 * Le mois décalé de `nombre` mois. Négatif pour reculer.
 *
 * En arithmétique entière sur (année × 12 + mois), sans jamais passer par un jour : ajouter
 * un mois au 31 janvier n'a pas de réponse, et la question ne se pose pas ici.
 */
export function ajouterMois(mois, nombre) {
    const { annee, numero } = partiesDuMois(mois);
    const total = annee * 12 + (numero - 1) + nombre;
    const anneeCible = Math.floor(total / 12);
    const moisCible = total - anneeCible * 12 + 1;
    return `${String(anneeCible).padStart(4, "0")}-${String(moisCible).padStart(2, "0")}`;
}
/** Combien de mois séparent deux mois. Positif quand `fin` est postérieur. */
export function moisEntre(debut, fin) {
    const a = partiesDuMois(debut);
    const b = partiesDuMois(fin);
    return (b.annee - a.annee) * 12 + (b.numero - a.numero);
}
/**
 * Les abréviations françaises des mois, telles qu'elles s'écrivent.
 *
 * Écrites une par une plutôt que tronquées à la machine. Une coupe à quatre lettres donne
 * « octo. », « nove. », « déce. » — vu à l'écran le 21/09/2026 sur le carnet de facturation,
 * et c'est le genre de détail qui fait douter du reste du tableau.
 */
const MOIS_ABREGES = [
    "janv.",
    "févr.",
    "mars",
    "avr.",
    "mai",
    "juin",
    "juil.",
    "août",
    "sept.",
    "oct.",
    "nov.",
    "déc.",
];
/** `sept. 2026` — pour un en-tête de colonne, où la place manque. */
export function formaterMoisCourt(mois) {
    const { annee, numero } = partiesDuMois(mois);
    return `${MOIS_ABREGES[numero - 1] ?? ""} ${String(annee)}`;
}
//# sourceMappingURL=calendrier.js.map